import type { BaseEnvelope, BasePatchOp } from './base-patch';
import type { AppliedFrame, AppliedTouchedRow } from '../transport/applied-frame';
import type { ActionComputedDelta, ComputedEntityDelta } from './action-computed-delta';
import { fromBaseRecord } from './from-base-context';
import { readItemField } from './item-field-reader';
import { encounterCreatureField, encounterField } from '../../shared/array-types';
import { abilityEntryId, rowAbilityChargeBuckets, rowAbilityChargeUpdates } from '../ability/charges/charge-entry';
import { relocationDefinitionsUnchanged } from './saved-creature-relocation';
import { encounterMemberInput, encounterMemberSharedInputs } from './encounter-member-input';
import { mergeInventoryMetadata } from './inventory-shared-catalog';
import { entityRuntimeMetadata } from './entity-runtime-metadata';
import { createFromBasePlayerGrantContext } from './from-base-player-grants';
import { createFromBaseNpcGrantContext } from './from-base-npc-stages';
import { createAbilityConditionEvaluator } from './ability-conditions';
import { readAbilityEntryId } from './inventory-ability-grant-helpers';
import { readLayerListEntryUuid } from './inventory-raw-readers';
import { meetsAbilityLevelRequirements } from './ability-level-requirement-gate';
import { savedGrantDependencyDelta } from './saved-grant-dependencies';
import { integerCompileBorrowedRow, integerCompileResult, putIntegerCompilePath, removeIntegerCompilePath } from './integer-compute-slots';
import { workerTreePatch } from './worker-tree-patch';
import { phpJsonInt, phpJsonTruthy } from './php-json-values';
import type { FromBasePlayerInput, FromBaseRow as Row } from './from-base-context';
import type { FromBasePlayerResourceMetadata } from './from-base-player-resources';

const upper = (value: unknown): string => String(value ?? '').toUpperCase();
const idOf = (row: Record<string, unknown>): string => upper(encounterCreatureField(row.encounter_creatures, 'encounter_creature_id'));
const record = (value: unknown): Row => fromBaseRecord(value) ? value : {};

/** Action deltas replace resized source lists; the worker's length opcode is transport-specific. */
function chargeDelta(before: unknown, after: unknown): ComputedEntityDelta {
    const patch = workerTreePatch(before, after);
    const arrays = patch.set.filter(({ path }) => path[path.length - 1] === 'length').map(({ path }) => path.slice(0, -1));
    const inside = (path: string[]) => arrays.some(parent => parent.every((key, index) => path[index] === key));
    return { set: [...patch.set.filter(({ path }) => !inside(path)), ...arrays.map(path => ({ path,
        value: path.reduce<unknown>((node, key) => (node as Row)[key], after) }))],
        unset: patch.unset.filter(path => !inside(path)) };
}

/** Borrow the member and project only this pool's leaves and the sources whose median gate can change. */
async function chargeProjection(member: Row, beforeBase: FromBasePlayerInput, afterBase: FromBasePlayerInput,
    changes: readonly AppliedTouchedRow[], metadata: FromBasePlayerResourceMetadata): Promise<Row> {
    const work = integerCompileBorrowedRow(member), pools = new Set<string>();
    for (const { row } of changes) {
        const item = upper(readItemField(row, 'item_id'));
        pools.add(item);
        const updates = rowAbilityChargeUpdates(member, item, phpJsonInt(readItemField(row, 'charges')));
        for (const { path, value } of updates.set) putIntegerCompilePath(work, path, value);
        for (const path of updates.unset) removeIntegerCompilePath(work, path);
    }
    let host = integerCompileResult(work) as Row;
    // The sender may already display the optimistic count. Let the ordinary
    // condition reader obtain the before-count from held raw rows instead.
    const priorWork = integerCompileBorrowedRow(member);
    for (const { value, path } of rowAbilityChargeBuckets(member)) if (pools.has(abilityEntryId(value))) {
        if (record(record(value).charges).current !== undefined) removeIntegerCompilePath(priorWork, [...path, 'charges', 'current']);
        if ('ability_charges' in record(value)) removeIntegerCompilePath(priorWork, [...path, 'ability_charges']);
    }
    const priorHost = integerCompileResult(priorWork) as Row;
    const context = (base: FromBasePlayerInput, row: Row) => fromBaseRecord(base.player_characters)
        ? createFromBasePlayerGrantContext(base, metadata, row) : createFromBaseNpcGrantContext(base as unknown as Row, metadata, row);
    const before = context(beforeBase, priorHost), after = context(afterBase, host);
    const priorConditions = createAbilityConditionEvaluator(before.deps), nextConditions = createAbilityConditionEvaluator(after.deps);
    const eligible = (definition: Parameters<typeof nextConditions.check>[0], entry: unknown, holder: Row): boolean => {
        const ctx = holder === priorHost ? before : after, conditions = holder === priorHost ? priorConditions : nextConditions;
        const pc = record(afterBase.player_characters), source = upper(readItemField(record(entry), 'source'));
        let classLevel: number | null = null;
        for (let slot = 1; slot <= 20; slot++) if ([`class_${slot}`, `subclass_${slot}`].some(key => upper(readItemField(pc, key)) === source))
            classLevel = phpJsonInt(readItemField(pc, `class_level_${slot}`));
        const requirement = (key: string) => ctx.readers.layerValue(definition, key) == null ? null : phpJsonInt(ctx.readers.layerValue(definition, key));
        return meetsAbilityLevelRequirements({ isPlayerCharacter: Boolean(readItemField(pc, 'item_id')),
            totalLevel: phpJsonInt(ctx.readers.layerValue(holder, 'total_level')), sourceClassLevel: classLevel,
            requiresLevel: requirement('requires_level'), requiresClassLevel: requirement('requires_class_level'),
            statusDelivered: phpJsonTruthy(readItemField(record(entry), 'granted_by_status_id')) }) && conditions.check(definition, holder);
    };
    let changed = false;
    for (const key of ['feat', 'passive_ability']) {
        const entries = Object.values(after.readers.layerEntries(host, key));
        for (const [index, entry] of entries.entries()) {
            const id = readAbilityEntryId(entry, { itemField: after.readers.itemField, resolveCarrierRef: after.definition });
            const raw = record(afterBase.catalog)[upper(id)];
            const pool = after.readers.layerValue(fromBaseRecord(raw) ? raw : null, 'ability_active_if_ability_charge_is_at_median');
            if (!pools.has(upper(readLayerListEntryUuid(pool)))) continue;
            const definition = after.definition(id);
            if (!definition) continue;
            const active = eligible(definition, entry, host);
            if (active === eligible(definition, entry, priorHost)) continue;
            changed = true;
            const abilities = record(record(record(host.computed).creature).abilities);
            const root = key in abilities ? ['computed', 'creature', 'abilities'] : ['item_layer_types'];
            putIntegerCompilePath(work, [...root, key, String(index), 'passive_conditions_active'], active);
        }
    }
    if (changed) {
        host = integerCompileResult(work) as Row;
        host = (await savedGrantDependencyDelta(priorHost, host, before, after, metadata, eligible, beforeBase, afterBase)).host;
    }
    return host;
}

/** A saved pool echo owns its raw row and charge leaves, never a creature or room compile. */
export async function savedChargeFrame(envelope: BaseEnvelope, ops: readonly BasePatchOp[], computed: unknown): Promise<{ frame: AppliedFrame; inputs: Row } | null> {
    const writes = ops.filter(op => !['items', 'item_layer_types'].includes(op.table));
    if (!writes.length || !writes.every(op => op.table === 'charges' && op.op === 'upsert' && !op.inputs)
        || ops.some(op => ['items', 'item_layer_types'].includes(op.table) && !fromBaseRecord(record(envelope.base.catalog)[upper(op.row_id)]))
        || !await relocationDefinitionsUnchanged(envelope.base, ops)) return null;
    let inputs = encounterMemberSharedInputs(envelope.base);
    for (const op of ops) if (op.catalog_inputs) inputs = mergeInventoryMetadata(inputs, op.catalog_inputs);
    const bases = [envelope.base, ...(Array.isArray(envelope.base.encounter_creatures_arr)
        ? envelope.base.encounter_creatures_arr.filter(fromBaseRecord) : [])];
    const output = fromBaseRecord(computed) ? computed : {};
    const members = [output, ...(Array.isArray(output.encounter_creatures_arr) ? output.encounter_creatures_arr.filter(fromBaseRecord) : [])];
    const rows: AppliedTouchedRow[] = [], delta: ActionComputedDelta = {};
    for (const op of writes) {
        const prior = bases.flatMap(base => Array.isArray(base.charges) ? base.charges.filter(fromBaseRecord) : [])
            .find(row => upper(readItemField(row, 'charge_id')) === upper(op.row_id));
        const row = { ...prior, ...op.cols };
        const holder = upper(readItemField(row, 'charge_holder')), item = upper(readItemField(row, 'item_id'));
        const current = Number(readItemField(row, 'charges') ?? 0);
        // Pool removal/restoration and non-creature pools retain their existing lane.
        if (!holder || !item || !Number.isSafeInteger(current) || Number(readItemField(row, 'charge_deleted') ?? 0) !== 0
            || !bases.some(base => idOf(base) === holder)) return null;
        rows.push({ table: 'charges', row_id: op.row_id, row });
        const member = members.find(member => idOf(member) === holder);
        if (!member) { if (envelope.entity === 'inventory') continue; return null; }
    }
    for (const holder of new Set(rows.map(({ row }) => upper(readItemField(row, 'charge_holder'))))) {
        const member = members.find(member => idOf(member) === holder), base = bases.find(base => idOf(base) === holder);
        if (!member || !base) continue;
        const changes = rows.filter(({ row }) => upper(readItemField(row, 'charge_holder')) === holder);
        const charges = new Map((Array.isArray(base.charges) ? base.charges.filter(fromBaseRecord) : [])
            .map(row => [upper(readItemField(row, 'charge_id')), row]));
        for (const row of changes) charges.set(upper(row.row_id), row.row);
        const contextual = encounterMemberInput(envelope.base, base) as unknown as FromBasePlayerInput;
        const nextBase = { ...contextual, ...inputs, charges: [...charges.values()] } as FromBasePlayerInput;
        const projected = await chargeProjection(member, contextual, nextBase, changes,
            entityRuntimeMetadata(envelope.metadata as Parameters<typeof entityRuntimeMetadata>[0]).player);
        const patch = chargeDelta(member.computed, projected.computed);
        const mirrorKeys = Object.keys(projected).filter(key => key !== 'computed' && projected[key] !== member[key]);
        patch.mirrors = chargeDelta(Object.fromEntries(mirrorKeys.map(key => [key, member[key]])),
            Object.fromEntries(mirrorKeys.map(key => [key, projected[key]])));
        delta[holder] = patch;
    }
    return { inputs, frame: { kind: 'applied', operation_id: `saved-charge:${envelope.entity}:${envelope.entity_id}:${ops[0]!.base_rev}`,
        encounter_id: upper(encounterField(envelope.base, 'encounter_id') ?? encounterField(envelope.base.encounter_context, 'encounter_id'))
            || envelope.entity_id, actor_id: '', rev: 0, member_revs: {}, rows,
        computed: {}, computed_delta: delta, new_entities: [], deletes: [] } };
}
